import { cva, type VariantProps } from 'class-variance-authority';
import {
  SPACE_GAP,
  SPACE_GAP_LG,
  SPACE_GAP_X,
  SPACE_MT,
  SPACE_PB,
  SPACE_PL,
  SPACE_PT,
  SPACE_PX,
} from '@/hen-ui/utils/space-steps';
import type {
  SubNavLabelStyle,
  SubNavLinkHover,
  SubNavLinkStyle,
  SubNavMobileBackColor,
  SubNavMobileHeader,
  SubNavMobileHeaderDivider,
  SubNavMobileItemSize,
  SubNavMobileLinkWeight,
  SubNavPanelStyle,
} from './types';

/**
 * HEN SubNav variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 */
export const subNavVariants = cva('w-full overflow-hidden bg-white', {
  variants: {
    rounded: {
      true: 'rounded-nav-panel',
      false: 'rounded-none',
    },
    shadow: {
      true: 'shadow-xl',
      false: 'shadow-none',
    },
  },
  defaultVariants: {
    rounded: true,
    shadow: true,
  },
});

export type SubNavVariantProps = VariantProps<typeof subNavVariants>;

export const SUBNAV_LABEL_STYLES = [
  'heading',
  'subtext',
] as const satisfies readonly SubNavLabelStyle[];
export const SUBNAV_LINK_STYLES = [
  'subtext',
  'body',
] as const satisfies readonly SubNavLinkStyle[];
export const SUBNAV_LINK_HOVERS = [
  'underline',
  'none',
  'primary',
] as const satisfies readonly SubNavLinkHover[];
export const SUBNAV_MOBILE_HEADERS = [
  'compact',
  'title',
] as const satisfies readonly SubNavMobileHeader[];
export const SUBNAV_MOBILE_ITEM_SIZES = [
  'compact',
  'touch',
] as const satisfies readonly SubNavMobileItemSize[];
export const SUBNAV_MOBILE_LINK_WEIGHTS = [
  'regular',
  'medium',
  'bold',
] as const satisfies readonly SubNavMobileLinkWeight[];
export const SUBNAV_MOBILE_HEADER_DIVIDERS = [
  'subtle',
  'divider',
  'strong',
  'accent',
  'none',
] as const satisfies readonly SubNavMobileHeaderDivider[];
export const SUBNAV_MOBILE_BACK_COLORS = [
  'accent',
  'neutral',
] as const satisfies readonly SubNavMobileBackColor[];

export const SUBNAV_LINK_WEIGHT_CLASS: Readonly<
  Record<SubNavMobileLinkWeight, string>
> = {
  regular: 'font-normal',
  medium: 'font-medium',
  bold: 'font-bold',
};

const PANEL_PADDING_X = 'px-lg lg:px-xl xl:px-2xl';
const PANEL_PADDING_TOP = 'pt-lg lg:pt-xl xl:pt-2xl';
const PANEL_PADDING_BOTTOM = 'pb-lg lg:pb-xl xl:pb-2xl';

const LABEL_TEXT: Record<SubNavLabelStyle, string> = {
  heading: 'text-heading font-bold',
  subtext: 'text-subtext font-normal',
};

const LINK_SIZE: Record<SubNavLinkStyle, string> = {
  subtext: 'text-b4 lg:text-b5 xl:text-b4',
  body: 'text-b5',
};

/** Padding around the desktop panel content. */
export function subNavPanelPaddingClass({
  paddingX,
  paddingY,
  paddingBottom = paddingY,
}: SubNavPanelStyle = {}): string {
  return [
    paddingX ? SPACE_PX[paddingX] : PANEL_PADDING_X,
    paddingY ? SPACE_PT[paddingY] : PANEL_PADDING_TOP,
    paddingBottom ? SPACE_PB[paddingBottom] : PANEL_PADDING_BOTTOM,
  ].join(' ');
}

export function subNavColumnGapClass({
  columnGap = '2xl',
}: SubNavPanelStyle = {}): string {
  return SPACE_GAP[columnGap];
}

/** Inline menu content sits one column gap after the last column. */
export function subNavInlineOffsetClass({
  columnGap = '2xl',
}: SubNavPanelStyle = {}): string {
  return SPACE_PL[columnGap];
}

/**
 * Fixed columns with inline menu content: the content keeps one column gap
 * after the last column, and wraps below the columns when the row is too
 * narrow for both.
 */
export function subNavInlineWrapClass({
  columnGap = '2xl',
}: SubNavPanelStyle = {}): string {
  return `flex-wrap gap-y-lg ${SPACE_GAP_X[columnGap]}`;
}

/** Gap and size of a column's link list. */
export function subNavLinkListClass({
  linkGap = 'xs',
  linkStyle = 'subtext',
}: SubNavPanelStyle = {}): string {
  return `${SPACE_GAP_LG[linkGap]} ${LINK_SIZE[linkStyle]}`;
}

/** Color for a column link; `''` keeps the link's own subtext color. */
export function subNavLinkColorClass({
  linkStyle = 'subtext',
}: SubNavPanelStyle = {}): string {
  return linkStyle === 'body' ? 'text-body' : '';
}

export function subNavLabelClass({
  labelTop = 'xxs',
  labelStyle = 'heading',
}: SubNavPanelStyle = {}): string {
  return `${SPACE_MT[labelTop]} ${LABEL_TEXT[labelStyle]}`;
}
